import { useCallback, useEffect, useRef, useState } from 'react';

import { durationToMs, isDurationZero, computeTimerProgress, toggleTimerMode } from './timeUtils';
import { hydrateTimerSessionForMount, readTimerSession, subscribeTimerCommands, writeTimerSession } from './timerSessionStore';
import {
  DEFAULT_TIMER_DURATION,
  DEFAULT_TIMER_MODE,
  type TimerDuration,
  type TimerMode,
  type TimerStatus,
} from './types';

export type UseTimerOptions = {
  initialTarget?: TimerDuration;
  initialMode?: TimerMode;
  /** 会话 id（如 block.id），用于跨 remount 保留计时状态 */
  sessionId?: string;
  onProgressTick?: (progress: number) => void;
  onTargetChange?: (next: TimerDuration) => void;
  onModeChange?: (next: TimerMode) => void;
  onComplete?: () => void;
};

export type UseTimerResult = {
  target: TimerDuration;
  setTarget: (next: TimerDuration) => void;
  mode: TimerMode;
  setMode: (next: TimerMode) => void;
  toggleMode: () => void;
  elapsedMs: number;
  status: TimerStatus;
  isSettingMode: boolean;
  targetMs: number;
  progress: number;
  hasTarget: boolean;
  hasStarted: boolean;
  toggleSettingMode: () => void;
  start: () => void;
  pause: () => void;
  togglePlayPause: () => void;
  reset: () => void;
};

type TimerRuntimeRefs = {
  elapsedBase: number;
  runningSince: number | null;
  completed: boolean;
};

function resolveInitialState(
  sessionId: string | undefined,
  initialTarget: TimerDuration,
  initialMode: TimerMode,
): {
  target: TimerDuration;
  mode: TimerMode;
  elapsedMs: number;
  status: TimerStatus;
  isSettingMode: boolean;
  refs: TimerRuntimeRefs;
} {
  const saved = sessionId ? hydrateTimerSessionForMount(sessionId) : undefined;
  if (saved) {
    return {
      target: saved.target,
      mode: saved.mode ?? initialMode,
      elapsedMs: saved.elapsedMs,
      status: saved.status,
      isSettingMode: saved.isSettingMode,
      refs: {
        elapsedBase: saved.elapsedBase,
        runningSince: saved.runningSince,
        completed: saved.status === 'completed',
      },
    };
  }
  return {
    target: initialTarget,
    mode: initialMode,
    elapsedMs: 0,
    status: 'idle',
    isSettingMode: false,
    refs: { elapsedBase: 0, runningSince: null, completed: false },
  };
}

export function useTimer(options: UseTimerOptions = {}): UseTimerResult {
  const {
    initialTarget = DEFAULT_TIMER_DURATION,
    initialMode = DEFAULT_TIMER_MODE,
    sessionId,
    onProgressTick,
    onTargetChange,
    onModeChange,
    onComplete,
  } = options;

  const initial = useRef(resolveInitialState(sessionId, initialTarget, initialMode)).current;

  const [target, setTargetState] = useState<TimerDuration>(initial.target);
  const [mode, setModeState] = useState<TimerMode>(initial.mode);
  const [elapsedMs, setElapsedMs] = useState(initial.elapsedMs);
  const [status, setStatus] = useState<TimerStatus>(initial.status);
  const [isSettingMode, setIsSettingMode] = useState(initial.isSettingMode);

  const elapsedBaseRef = useRef(initial.refs.elapsedBase);
  const startedAtRef = useRef<number | null>(initial.refs.runningSince);
  const completedRef = useRef(initial.refs.completed);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onTargetChangeRef = useRef(onTargetChange);
  onTargetChangeRef.current = onTargetChange;
  const onModeChangeRef = useRef(onModeChange);
  onModeChangeRef.current = onModeChange;
  const onProgressTickRef = useRef(onProgressTick);
  onProgressTickRef.current = onProgressTick;

  const emitProgress = useCallback((elapsed: number) => {
    const ms = durationToMs(targetRef.current);
    onProgressTickRef.current?.(computeTimerProgress(ms, elapsed));
  }, []);

  const targetRef = useRef(target);
  targetRef.current = target;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const statusRef = useRef(status);
  statusRef.current = status;
  const isSettingModeRef = useRef(isSettingMode);
  isSettingModeRef.current = isSettingMode;
  const elapsedMsRef = useRef(elapsedMs);
  elapsedMsRef.current = elapsedMs;

  const syncElapsed = useCallback(() => {
    if (startedAtRef.current == null) return elapsedBaseRef.current;
    return elapsedBaseRef.current + (performance.now() - startedAtRef.current);
  }, []);

  const persistSession = useCallback(() => {
    if (!sessionId) return;
    const currentStatus = statusRef.current;
    const running = currentStatus === 'running';
    const elapsed = running ? syncElapsed() : elapsedMsRef.current;
    writeTimerSession(sessionId, {
      target: targetRef.current,
      mode: modeRef.current,
      elapsedMs: elapsed,
      status: currentStatus,
      isSettingMode: isSettingModeRef.current,
      elapsedBase: running ? elapsedBaseRef.current : elapsed,
      runningSince: running ? startedAtRef.current : null,
    });
  }, [sessionId, syncElapsed]);

  const applyStatus = useCallback((next: TimerStatus) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  useEffect(() => {
    setTargetState((prev) => {
      const same =
        prev.hours === initialTarget.hours &&
        prev.minutes === initialTarget.minutes &&
        prev.seconds === initialTarget.seconds;
      if (same) return prev;
      if (statusRef.current === 'running') return prev;
      return initialTarget;
    });
  }, [initialTarget.hours, initialTarget.minutes, initialTarget.seconds]);

  useEffect(() => {
    setModeState((prev) => {
      if (prev === initialMode) return prev;
      if (statusRef.current === 'running') return prev;
      return initialMode;
    });
  }, [initialMode]);

  useEffect(() => {
    return () => {
      persistSession();
    };
  }, [persistSession]);

  const finishTimer = useCallback((finalElapsed: number) => {
    startedAtRef.current = null;
    elapsedBaseRef.current = finalElapsed;
    elapsedMsRef.current = finalElapsed;
    isSettingModeRef.current = false;
    setElapsedMs(finalElapsed);
    setIsSettingMode(false);
    applyStatus('completed');
    emitProgress(finalElapsed);
    if (!completedRef.current) {
      completedRef.current = true;
      onCompleteRef.current?.();
    }
    persistSession();
  }, [applyStatus, emitProgress, persistSession]);

  const pause = useCallback(() => {
    if (statusRef.current !== 'running') return;
    const current = syncElapsed();
    startedAtRef.current = null;
    elapsedBaseRef.current = current;
    elapsedMsRef.current = current;
    setElapsedMs(current);
    applyStatus('paused');
    emitProgress(current);
    persistSession();
  }, [applyStatus, emitProgress, syncElapsed, persistSession]);

  const start = useCallback(() => {
    if (!durationToMs(targetRef.current) || statusRef.current === 'running') return;
    completedRef.current = false;
    if (statusRef.current === 'completed') {
      elapsedBaseRef.current = 0;
      elapsedMsRef.current = 0;
      setElapsedMs(0);
      emitProgress(0);
    }
    startedAtRef.current = performance.now();
    isSettingModeRef.current = false;
    setIsSettingMode(false);
    applyStatus('running');
    emitProgress(elapsedBaseRef.current);
    persistSession();
  }, [applyStatus, emitProgress, persistSession]);

  const reset = useCallback(() => {
    startedAtRef.current = null;
    elapsedBaseRef.current = 0;
    elapsedMsRef.current = 0;
    completedRef.current = false;
    isSettingModeRef.current = false;
    setElapsedMs(0);
    setIsSettingMode(false);
    applyStatus('idle');
    emitProgress(0);
    persistSession();
  }, [applyStatus, emitProgress, persistSession]);

  const toggleSettingMode = useCallback(() => {
    setIsSettingMode((prev) => {
      const next = !prev;
      isSettingModeRef.current = next;
      if (next && statusRef.current === 'running') {
        const current = syncElapsed();
        startedAtRef.current = null;
        elapsedBaseRef.current = current;
        elapsedMsRef.current = current;
        setElapsedMs(current);
        applyStatus('paused');
        emitProgress(current);
      }
      queueMicrotask(persistSession);
      return next;
    });
  }, [applyStatus, emitProgress, syncElapsed, persistSession]);

  const togglePlayPause = useCallback(() => {
    if (statusRef.current === 'running') {
      pause();
      return;
    }
    start();
  }, [pause, start]);

  const setTarget = useCallback((next: TimerDuration) => {
    setTargetState(next);
    onTargetChangeRef.current?.(next);
    if (isDurationZero(next)) {
      reset();
      return;
    }
    persistSession();
  }, [reset, persistSession]);

  const applyModeChange = useCallback((next: TimerMode) => {
    modeRef.current = next;
    setModeState(next);
    onModeChangeRef.current?.(next);
    startedAtRef.current = null;
    elapsedBaseRef.current = 0;
    elapsedMsRef.current = 0;
    completedRef.current = false;
    setElapsedMs(0);
    applyStatus('idle');
    emitProgress(0);
    persistSession();
  }, [applyStatus, emitProgress, persistSession]);

  const setMode = useCallback((next: TimerMode) => {
    if (next === modeRef.current) return;
    applyModeChange(next);
  }, [applyModeChange]);

  const toggleMode = useCallback(() => {
    applyModeChange(toggleTimerMode(modeRef.current));
  }, [applyModeChange]);

  useEffect(() => {
    if (!sessionId) return;
    return subscribeTimerCommands(sessionId, (command) => {
      if (command === 'togglePlayPause') togglePlayPause();
      else if (command === 'reset') reset();
    });
  }, [sessionId, togglePlayPause, reset]);

  const targetMs = durationToMs(target);
  const hasTarget = targetMs > 0;
  const progress = computeTimerProgress(targetMs, elapsedMs);
  const hasStarted =
    elapsedMs > 0 || status === 'running' || status === 'paused' || status === 'completed';

  useEffect(() => {
    if (status === 'running') return;
    emitProgress(elapsedMs);
  }, [elapsedMs, emitProgress, status]);

  useEffect(() => {
    if (status !== 'running') return;

    let rafId = 0;
    let lastPersist = 0;
    const tick = () => {
      const current = syncElapsed();
      if (targetMs > 0 && current >= targetMs) {
        finishTimer(targetMs);
        return;
      }
      setElapsedMs(current);
      emitProgress(current);
      const now = performance.now();
      if (sessionId && now - lastPersist >= 250) {
        lastPersist = now;
        persistSession();
      }
      rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafId);
  }, [emitProgress, finishTimer, persistSession, sessionId, status, syncElapsed, targetMs]);

  return {
    target,
    setTarget,
    mode,
    setMode,
    toggleMode,
    elapsedMs,
    status,
    isSettingMode,
    targetMs,
    progress,
    hasTarget,
    hasStarted,
    toggleSettingMode,
    start,
    pause,
    togglePlayPause,
    reset,
  };
}
